import { Box, Stack, DialogContent } from '@mui/material'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import React, { useState } from 'react'
import { t } from 'i18next'

import FilterIcon from '@hapana/shared/web/styles/assets/svgs/filter-small.svg'
import {
  CLOSE_TYPES,
  SIZES,
} from '../../../../../components/shared/dialog/dialog-layout/dialog-layout.constants'
import Dialog from '../../../../../components/shared/dialog/dialog.component'
import Accordion from '../../../../../components/shared/accordion/accordion.component'
import { commonStyles } from '../../../../../styles/common.styles'
import CloseIcon from '@hapana/shared/web/styles/assets/svgs/close-icon.svg'
import { useDialog } from '../../../../../components/shared/dialog/dialog.hooks'

import { FILTER_MODES, MOBILE_FILTER_TYPES } from '../../table.constants'
import { MUI_SELECT_PROPS } from '../../../../../../../web/core/src/pages/user-access/users-access-overview/users-access-overview.constants'

import { styles } from './table-mobile-filter.styles'
import CheckboxGroupFilter from './table-filter-checkbox.component'
import RadioGroupFilter from './table-filter-radio.component'
import TableFilter from './table-filter.component'

const getInitialFilters = (columns, appliedFilters) => {
  const initialFilters = {}

  columns.forEach((column) => {
    if (column.hasFilter) {
      const appliedFilterKey = column.id
      const appliedValue = appliedFilters[appliedFilterKey]?.value

      if (column.mobileFilterType === MOBILE_FILTER_TYPES.checkbox) {
        initialFilters[column.id] = Array.isArray(appliedValue)
          ? appliedValue
          : appliedValue
            ? [appliedValue]
            : []
      } else {
        initialFilters[column.id] = Array.isArray(appliedValue) ? appliedValue[0] : appliedValue
      }
    }
  })

  return initialFilters
}

const TableMobileFilterDialog = ({
  closeDialog,
  columns,
  setFilter,
  filterMode,
  mobileFilterTitle,
  onClearMobileFilters,
  onFilterChange,
}) => {
  const { t } = useTranslation('common')
  const { dialogState, popDialog, pushDialog } = useDialog()

  const [tempFilters, setTempFilters] = useState(() =>
    getInitialFilters(columns, dialogState?.appliedFilters || {})
  )

  // ✅ RESTORE ORIGINAL LOGIC - don't mess with this!
  const handleSetFilter = (columnId, filterValues) => {
    const column = columns.find((col) => col.id === columnId)
    if (!column) return

    const transformedValue = (() => {
      switch (column.mobileFilterType) {
        case MOBILE_FILTER_TYPES.checkbox:
          return Array.isArray(filterValues)
            ? filterValues.map((option) => option.value)
            : filterValues

        case MOBILE_FILTER_TYPES.radio:
          return filterValues

        default:
          // ✅ KEEP ORIGINAL LOGIC - only convert arrays for non-date filters
          if (column.filterSelectType === 'date') {
            return filterValues // Keep single dates as-is
          }
          return Array.isArray(filterValues) ? filterValues[0] : filterValues
      }
    })()

    setTempFilters((prev) => ({
      ...prev,
      [columnId]: transformedValue,
    }))

    // Call onFilterChange callback if provided
    if (onFilterChange) {
      onFilterChange(columnId, transformedValue)
    }
  }

  const hasTempFilters = Object.values(tempFilters).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  )

  const handleClearFilters = () => {
    const clearedColumns = columns.map((column) => ({
      ...column,
      filterValue: undefined,
    }))

    onClearMobileFilters()
    popDialog()
    pushDialog(
      TableMobileFilterDialog,
      {
        closeDialog,
        columns: clearedColumns,
        setFilter,
        filterMode,
        mobileFilterTitle,
        onClearMobileFilters,
        onFilterChange,
        key: Date.now(),
      },
      {
        appliedFilters: {},
      }
    )
  }

  const handleApply = () => {
    Object.entries(tempFilters).forEach(([columnId, value]) => {
      setFilter(columnId, value)
    })
    closeDialog()
  }

  const renderFilterComponent = (column) => {
    const currentValue = tempFilters[column.id]
    const appliedFilterKey = column.id
    const appliedValue = dialogState?.appliedFilters?.[appliedFilterKey]?.value

    if (column.mobileFilterType === MOBILE_FILTER_TYPES.checkbox) {
      return (
        <CheckboxGroupFilter
          column={{
            ...column,
            filterValue: currentValue || appliedValue,
          }}
          setFilter={(id, value) => handleSetFilter(id, value)}
          filterMode={filterMode}
          disabled={column.disabled}
        />
      )
    } else if (column.mobileFilterType === MOBILE_FILTER_TYPES.radio) {
      return (
        <RadioGroupFilter
          column={{
            ...column,
            filterValue: currentValue || appliedValue,
          }}
          setFilter={(id, value) => handleSetFilter(id, value)}
          filterMode={filterMode}
          disabled={column.disabled}
        />
      )
    } else {
      const filterValue = currentValue !== undefined ? currentValue : appliedValue

      // ✅ RESTORE ORIGINAL LOGIC - only convert for non-date filters
      const finalValue =
        column.filterSelectType === 'date'
          ? filterValue // Keep single dates as-is
          : Array.isArray(filterValue)
            ? filterValue[0]
            : filterValue

      return (
        <TableFilter
          column={{
            ...column,
            filterValue: finalValue,
            serverSideFilterOptions: column.serverSideFilterOptions || [],
          }}
          setFilter={(id, value) => handleSetFilter(id, value)}
          filterMode={filterMode}
          filterSelectType={column.filterSelectType}
          muiSelectProps={MUI_SELECT_PROPS}
          disabled={column.disabled}
        />
      )
    }
  }

  const isFilterActive = (columnId) => {
    const hasOnlyOneFilter = columns.filter((item) => item.hasFilter).length === 1

    if (hasOnlyOneFilter) {
      return true
    }

    const value = tempFilters[columnId]
    return Array.isArray(value) ? value.length > 0 : Boolean(value)
  }

  return (
    <Dialog
      title={mobileFilterTitle}
      titleIcon={FilterIcon}
      closeType={CLOSE_TYPES.TEXT}
      size={SIZES.SM}
      closeAction={closeDialog}
      primaryAction={{
        label: t('components.table.filters.apply'),
        action: handleApply,
      }}
      secondaryAction={
        hasTempFilters && {
          label: t('components.table.filters.clear'),
          action: handleClearFilters,
          icon: CloseIcon,
        }
      }
    >
      <DialogContent sx={styles.content}>
        <Stack>
          <Box sx={[styles.columnsFilters, commonStyles.fullWidth]}>
            {columns
              .filter((item) => item.hasFilter)
              .map((column) => (
                <Accordion
                  key={column.id}
                  groupedRows
                  borderBottomSummary
                  label={column.Header}
                  contentStyles={styles.contentStyle}
                  containerStyles={styles.containerStyles}
                  summaryStyles={styles.summaryStyle}
                  content={<Box sx={styles.filterWrapper}>{renderFilterComponent(column)}</Box>}
                  id={`accordion-${column.id}`}
                  isOpen={isFilterActive(column.id)}
                />
              ))}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

TableMobileFilterDialog.propTypes = {
  closeDialog: PropTypes.func,
  columns: PropTypes.array.isRequired,
  setFilter: PropTypes.func.isRequired,
  filterMode: PropTypes.oneOf(Object.values(FILTER_MODES)).isRequired,
  mobileFilterTitle: PropTypes.string.isRequired,
  onClearMobileFilters: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func,
}

TableMobileFilterDialog.defaultProps = {
  mobileFilterTitle: t('common:components.table.filters.label'),
}

export default TableMobileFilterDialog
